'use client';

import Link from '@lichtblick/link';
import classNames from 'classnames';
import type { FC, HTMLAttributeAnchorTarget, JSX, MouseEventHandler, ReactNode } from 'react';
import type {} from '../../react';

import styles from './text-cta.module.scss';

export type TextCTAVariant = 'default' | 'plain' | 'thin';

type TextCTABaseBaseProps = {
  className?: string;
  'data-dd-action-name'?: string;
  'data-testid'?: string;
  inlineLink?: boolean;
  slim?: boolean;
  tabIndex?: number;
  variant?: TextCTAVariant;
};

type TextCTAWithChildrenProps = TextCTABaseBaseProps & {
  ariaLabel?: string;
  children: ReactNode;
  icon?: JSX.Element;
};
type TextCTAWithoutChildrenProps = TextCTABaseBaseProps & {
  ariaLabel: string;
  icon: JSX.Element;
};

type TextCTABaseProps = TextCTAWithChildrenProps | TextCTAWithoutChildrenProps;

type TextCTACommandProps =
  | {
      command?: never;
      commandFor?: never;
    }
  | {
      command: NonNullable<React.ButtonHTMLAttributes<HTMLButtonElement>['command']>;
      commandFor: NonNullable<React.ButtonHTMLAttributes<HTMLButtonElement>['commandfor']>;
      type: 'button';
    };

export type TextCTAButtonProps = TextCTACommandProps &
  TextCTABaseProps & {
    as: 'button';
    isDisabled?: boolean;
    isLoading?: boolean;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    type?: React.ButtonHTMLAttributes<undefined>['type'];
  };

export type TextCTAAnchorProps = TextCTABaseProps & {
  as: 'a';
  download?: boolean;
  href: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  target?: HTMLAttributeAnchorTarget;
};

export type TextCTAProps = TextCTAButtonProps | TextCTAAnchorProps;

export const TextCTA: FC<TextCTAProps> = (props) => {
  const {
    ariaLabel,
    as,
    'data-dd-action-name': ddAction,
    'data-testid': testId,
    icon,
    inlineLink = false,
    slim,
    tabIndex,
    variant = 'default',
  } = props;
  const children = 'children' in props ? props.children : undefined;

  const className = classNames(
    'text-cta',
    styles.cta,
    variant !== 'default' && styles[variant],
    slim && styles.slim,
    inlineLink && styles.inline,
    props.className,
  );

  if (as === 'a') {
    const { download = false, href, onClick, target } = props;

    return (
      <Link
        aria-label={ariaLabel}
        className={className}
        data-dd-action-name={ddAction}
        data-testid={testId}
        download={download}
        href={href}
        onClick={onClick}
        tabIndex={tabIndex}
        target={target}
      >
        {icon && <span className={styles.icon}>{icon}</span>}
        {children && <span className={styles.text}>{children}</span>}
      </Link>
    );
  }

  const { isDisabled, isLoading, onClick, type, command, commandFor } = props;

  return (
    <button
      aria-label={ariaLabel}
      className={className}
      data-dd-action-name={ddAction}
      data-testid={testId}
      disabled={isDisabled || isLoading}
      onClick={onClick}
      tabIndex={tabIndex}
      type={type}
      command={command}
      commandfor={commandFor}
    >
      {icon && <span className={styles.icon}>{icon}</span>}
      {children && <span className={styles.text}>{children}</span>}
    </button>
  );
};
